/**
 * `track()` — the sanctioned analytics entry point for app code.
 *
 * As of Wave 1 · P2 [3/5] this delegates to the `@heidi/analytics`
 * pipeline (enrichment + adapter fan-out live there; app wiring in
 * `./wiring.ts` and the SDK-owning init sites supplies context + adapters).
 * This facade exists to keep the strong app-side `TrackingEventV2` typing on
 * call sites and as the single import path the R1 containment ratchet points
 * new code at.
 *
 * IT DOES NOT BURN DOWN INTO A PURE RE-EXPORT, and the plan to codemod call
 * sites onto the package door directly is retired until the door's signature
 * changes. Measured against the door as it stands:
 *
 *   - `track({ event_name, props })` with a typed props interface does not
 *     compile against the door — `PanelEventProps` has no index signature and
 *     so is not assignable to `Record<string, unknown>`. A call site cannot
 *     move by rewriting its specifier; it would need a cast, at every site.
 *   - the door is deliberately string-typed (`event_name: string`), so it
 *     accepts any props for any event name. Moving the call sites would
 *     silently delete the discriminated-union check that today makes an
 *     event/props mismatch a compile error.
 *
 * The union migrates out of the app when event declarations become a
 * contributions surface; the facade is what keeps the checking until then.
 */
import { type TrackingEventV2 } from '@/models/analytics/eventTypes';
import { track as trackViaPipeline } from '@heidi/analytics';

export type TrackEvent = TrackingEventV2 & {
  /** Additional properties that are sent to Amplitude only. */
  amplitudeProperties?: Record<string, unknown>;
  /** Optional URL to override page URL in analytics (useful for preserving attribution params) */
  pageUrl?: string | null;
  /**
   * Optional destination control. Datadog always fires. Snowplow fires unless
   * this call passes `snowplow: false` or the event name is on the app's
   * denylist (`./snowplow-suppression.ts`), which the pipeline applies.
   */
  options?: {
    segment?: boolean;
    posthog?: boolean;
    amplitude?: boolean;
    snowplow?: boolean;
  };
};

export function track(trackingEvent: TrackEvent): void {
  // Structural bridge: the package door is deliberately string-typed
  // (`event_name: string`); the union narrows into it. Some union members'
  // props interfaces lack index signatures, so a direct assign is rejected —
  // the runtime shape is identical.
  trackViaPipeline({
    event_name: trackingEvent.event_name,
    props: trackingEvent.props as Record<string, unknown> | undefined,
    amplitudeProperties: trackingEvent.amplitudeProperties,
    pageUrl: trackingEvent.pageUrl,
    options: trackingEvent.options,
  });
}
